import React, { useState, useRef, useEffect, useCallback } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CloseIcon from '@mui/icons-material/Close'

import './UnifiedDropdown.css'

export interface OnChangeEventContext {
  value: any
  name?: string | null
}

interface OptionType {
  label: string
  value: any
  group?: string
  originalOption?: any
  [key: string]: any
}

interface GroupedOptionType {
  label?: string
  title?: string
  options: Array<any>
  [key: string]: any
}

export interface UnifiedDropdownProps {
  ariaLabel?: string | null
  ariaLabelledBy?: string | null
  beforeInput?: React.ReactNode
  /** @deprecated Use `onClear` instead. This prop is kept for API compatibility but has no effect. */
  // eslint-disable-next-line react/no-unused-prop-types
  clearable?: boolean
  /** Callback to clear the selected value. When provided, an X button is shown inside the dropdown whenever a value is selected. */
  onClear?: () => void
  clearInputOnBlur?: boolean
  delimiter?: string
  getOptionLabel?:
    | ((params: { option: any; labelKey?: string; selected?: boolean }) => string)
    | null
  grouped?: boolean
  id?: string | null
  inputId?: string
  isMulti?: boolean
  isFilterable?: boolean
  label?: string | null
  labelKey?: string
  menuAriaLabel?: string
  name?: string | null
  onBlur?: ((e: React.FocusEvent) => void) | null
  onChange?: ((e: OnChangeEventContext) => void) | null
  onFocus?: ((e: React.FocusEvent) => void) | null
  onKeyDown?: ((e: React.KeyboardEvent) => void) | null
  onOpen?: (() => void) | null
  onInputChange?: ((e: any) => void) | null
  onSelect?: ((e: OnChangeEventContext) => void) | null
  onStaticOptionClick?: ((e: OnChangeEventContext) => void) | null
  optionRenderer?: ((option: any) => React.ReactNode) | null
  options?: Array<any>
  placeholder?: string
  showInput?: boolean
  staticOption?: any
  value?: any
  valueKey?: string
  disabled?: boolean
  styleVariant?: string
  inputValue?: string
  valueComponent?: ((props: { option: any }) => React.ReactNode) | null
  /** Custom renderer for the closed single-select value (e.g. to append a sequence number to the label) */
  valueRenderer?: ((props: { option: any }) => React.ReactNode) | null
}

const UnifiedDropdown = (props: UnifiedDropdownProps) => {
  const {
    ariaLabel = null,
    ariaLabelledBy = null,
    beforeInput,
    clearInputOnBlur = false,
    delimiter = ', ',
    getOptionLabel = null,
    valueComponent = null,
    valueRenderer = null,
    grouped = false,
    id = null,
    inputId,
    isMulti = false,
    isFilterable = false,
    label = null,
    labelKey = 'label',
    menuAriaLabel,
    name = null,
    onBlur: onBlurProp = null,
    onChange = null,
    onFocus: onFocusProp = null,
    onKeyDown: onKeyDownProp = null,
    onOpen = null,
    onInputChange = null,
    onSelect = null,
    onStaticOptionClick = null,
    optionRenderer = null,
    options = [],
    onClear,
    placeholder = 'Select...',
    showInput,
    staticOption,
    value = null,
    valueKey = 'value',
    disabled = false,
    styleVariant = 'default',
    inputValue
  } = props
  const [menuOpen, setMenuOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [internalFilterText, setInternalFilterText] = useState('')
  const filterText = inputValue !== undefined ? inputValue : internalFilterText
  const [announcement, setAnnouncement] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const clearJustFiredRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const valueGroupRef = useRef<HTMLDivElement>(null)
  const filterInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [forceAnnouncementTrigger, setForceAnnouncementTrigger] = useState(0)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)

  // Scroll the focused option into view when keyboard navigation changes focusedIndex
  useEffect(() => {
    if (!menuOpen || focusedIndex < 0 || !menuRef.current) return

    const focusedEl = menuRef.current.querySelector<HTMLElement>('.crane-select-option.focused')
    if (focusedEl) {
      focusedEl.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, menuOpen])

  // Update container height when valueComponent is used
  useEffect(() => {
    if (valueComponent && isMulti && containerRef.current && valueGroupRef.current) {
      const valueGroupHeight = valueGroupRef.current.offsetHeight
      if (valueGroupHeight > 0) {
        const paddingBottom = 8 // matches inline style
        const border = 2 // top and bottom borders
        const calculatedHeight = valueGroupHeight + paddingBottom + border
        containerRef.current.style.height = `${calculatedHeight}px`
      } else {
        // Reset stale height when all chips are removed so the container
        // shrinks back to its CSS-defined size instead of staying oversized.
        containerRef.current.style.height = ''
      }
    }
  }, [valueComponent, isMulti, value])

  // Calculate menu position for term card dropdowns (which use position: fixed)
  // to prevent clipping by overflow containers
  useEffect(() => {
    const isTermCardDropdown = id?.endsWith('_new_item') || id?.endsWith('_new_placeholder')

    const updateMenuPosition = () => {
      if (menuOpen && isTermCardDropdown && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setMenuPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
    }

    if (menuOpen && isTermCardDropdown) {
      updateMenuPosition()

      let rafId: number | null = null

      // Update position on scroll using requestAnimationFrame for smooth updates
      const handleScroll = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId)
        }
        rafId = requestAnimationFrame(updateMenuPosition)
      }

      window.addEventListener('scroll', handleScroll, true)
      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        if (rafId !== null) {
          cancelAnimationFrame(rafId)
        }
      }
    }

    if (!menuOpen) {
      setMenuPosition(null)
    }

    return undefined
  }, [menuOpen, id])

  const dropdownOptions = React.useMemo(() => {
    // Helper to get label using getOptionLabel or fallback to labelKey
    const extractLabel = (opt: any): string => {
      if (getOptionLabel) {
        return getOptionLabel({ option: opt, labelKey })
      }
      return opt[labelKey!] || opt.label
    }

    // Helper to convert static option(s) to processed option format
    // Supports both single object and array of objects
    const processStaticOptions = (): OptionType[] => {
      if (!staticOption) return []
      const items = Array.isArray(staticOption) ? staticOption : [staticOption]
      return items.map((opt: any) => ({
        label: extractLabel(opt),
        value: opt[valueKey!] || opt.value,
        originalOption: opt,
        isStatic: true
      }))
    }

    if (!options || !options.length) {
      // If there are no options but there's a staticOption, show it
      if (staticOption) {
        return processStaticOptions()
      }
      return []
    }

    let processedOptions: OptionType[] = []

    if (grouped && options[0] && options[0].options) {
      processedOptions = (options as GroupedOptionType[]).flatMap((group) =>
        group.options
          ? group.options.map((opt) => ({
              ...opt,
              label: extractLabel(opt),
              value: opt[valueKey!] || opt.value,
              group: group.label || group.title,
              originalOption: opt
            }))
          : []
      )
    } else if (options[0] && options[0].label && options[0].value) {
      processedOptions = options.map((opt) => ({
        ...opt,
        label: getOptionLabel ? extractLabel(opt) : opt.label,
        originalOption: opt
      }))
    } else {
      processedOptions = options.map((o) => ({
        label: extractLabel(o),
        value: o[valueKey!],
        originalOption: o
      }))
    }

    // Add static options at the end if provided
    const staticOptions = processStaticOptions()
    if (staticOptions.length > 0) {
      processedOptions.push(...staticOptions)
    }

    // Only apply client-side filtering when using internal filter state.
    // When inputValue is externally controlled (not undefined), the parent manages
    // the options (e.g., server-side search), so client-side filtering is redundant
    // and can cause bugs (e.g., trailing spaces in search text not matching option labels).
    const shouldApplyClientFilter = isFilterable && inputValue === undefined

    return filterText && shouldApplyClientFilter
      ? processedOptions.filter((option) =>
          option.label.toLowerCase().includes(filterText.toLowerCase())
        )
      : processedOptions
  }, [
    options,
    grouped,
    labelKey,
    valueKey,
    filterText,
    isFilterable,
    getOptionLabel,
    staticOption,
    inputValue
  ])

  const selectedValues = React.useMemo(() => (isMulti ? value || [] : value), [isMulti, value])

  // Helper: Check if value is already a properly formatted option
  const isFormattedOption = (v: any): boolean => v && typeof v === 'object' && v.label && v.value

  // Helper: Normalize a single value to an option format for multi-select
  const normalizeMultiValue = useCallback(
    (v: any): OptionType | null => {
      if (isFormattedOption(v)) return v

      if (v && typeof v === 'object' && v[labelKey!] !== undefined) {
        return {
          label: v[labelKey!],
          value: v[valueKey!] !== undefined ? v[valueKey!] : v,
          originalOption: v
        }
      }

      const vValue = v && typeof v === 'object' && v[valueKey!] !== undefined ? v[valueKey!] : v
      return dropdownOptions.find((o) => o.value === vValue) || null
    },
    [dropdownOptions, labelKey, valueKey]
  )

  // Helper: Find option in dropdownOptions that matches the value
  const findMatchingOption = useCallback(
    (val: any): OptionType | null => {
      const valueToMatch = val && typeof val === 'object' ? val.value || val[valueKey!] || val : val

      return (
        dropdownOptions.find(
          (o) =>
            o.value === valueToMatch ||
            o.value === val ||
            (o.originalOption && o.originalOption[valueKey!] === valueToMatch) ||
            (o.originalOption &&
              val &&
              typeof val === 'object' &&
              o.originalOption[valueKey!] === val[valueKey!])
        ) || null
      )
    },
    [dropdownOptions, valueKey]
  )

  // Helper: Normalize single value to option format
  const normalizeSingleValue = useCallback(
    (val: any): OptionType | null => {
      if (!val) return null
      if (isFormattedOption(val)) return val

      if (val && typeof val === 'object' && val[labelKey!]) {
        return {
          label: val[labelKey!],
          value: val[valueKey!] || val.value || val
        }
      }

      const found = findMatchingOption(val)
      if (found) return found

      if (typeof val === 'string' || typeof val === 'number') {
        return { label: String(val), value: val }
      }

      return null
    },
    [findMatchingOption, labelKey, valueKey]
  )

  const selectedOptions = React.useMemo(() => {
    if (isMulti) {
      return selectedValues.length > 0
        ? selectedValues
            .map(normalizeMultiValue)
            .filter((opt: any) => opt !== null && opt !== undefined)
        : []
    }
    return normalizeSingleValue(selectedValues)
  }, [selectedValues, isMulti, normalizeMultiValue, normalizeSingleValue])

  const handleMenuOpen = () => {
    setMenuOpen(true)
    // For multi-select, don't auto-focus first item (checkboxes are used)
    // For single-select, focus first item for keyboard navigation
    setFocusedIndex(dropdownOptions.length > 0 && !isMulti ? 0 : -1)

    if (dropdownOptions.length === 0) {
      setAnnouncement(filterText ? 'No options match your search' : 'No options available')
    }

    // Focus filter input for any filterable dropdown (single or multi) so user can type (A11Y-1408)
    if (isFilterable) {
      setTimeout(() => {
        if (filterInputRef.current) {
          filterInputRef.current.focus()
        } else if (containerRef.current) {
          // For filterable dropdowns without a text input (e.g., filter-mode selects),
          // focus the combobox container so keyboard events (Escape, arrows) work
          containerRef.current.focus()
        }
      }, 0)
    } else if (isMulti && showInput) {
      setTimeout(() => {
        if (filterInputRef.current) {
          filterInputRef.current.focus()
        }
      }, 0)
    } else if (containerRef.current) {
      containerRef.current.focus()
    }

    if (onOpen) {
      onOpen()
    }
  }

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false)
    setFocusedIndex(-1)
    if (onInputChange) {
      onInputChange({ value: '' })
    }
    if (inputValue === undefined) {
      setInternalFilterText('')
    }
  }, [onInputChange, inputValue])

  // Close dropdown when clicking outside the entire component (wrapper includes combobox + menu)
  useEffect(() => {
    if (!menuOpen) return undefined

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        handleMenuClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen, handleMenuClose])

  const handleMenuToggle = (event: React.MouseEvent) => {
    if (disabled) return

    event.stopPropagation()
    if (menuOpen) {
      handleMenuClose()
    } else {
      handleMenuOpen()
    }
  }

  const handleOptionClick = (option: OptionType) => {
    if (disabled) return

    if (!option) return

    const optionToUse = option.originalOption || option

    // If this is a static option and onStaticOptionClick is provided, use that handler
    if (option.isStatic && onStaticOptionClick) {
      onStaticOptionClick({ value: optionToUse, name })
      handleMenuClose()
      focusContainer()
      return
    }

    if (isMulti) {
      const optionValueKey =
        optionToUse && typeof optionToUse === 'object' && optionToUse[valueKey!] !== undefined
          ? optionToUse[valueKey!]
          : option.value

      const isSelected = selectedValues.some((v: any) => {
        const vValueKey =
          v && typeof v === 'object' && v[valueKey!] !== undefined ? v[valueKey!] : v
        return vValueKey === optionValueKey
      })

      let newValue: any[]
      if (isSelected) {
        // When deselecting, filter out the clicked item
        newValue = selectedValues.filter((v: any) => {
          const vValueKey =
            v && typeof v === 'object' && v[valueKey!] !== undefined ? v[valueKey!] : v
          return vValueKey !== optionValueKey
        })
      } else {
        // When selecting, add the option
        newValue = [...selectedValues, optionToUse]
      }

      // Ensure all items in newValue are proper objects for parent components
      // that expect to access properties via keys (like FacetFilterMultiSelect)
      const normalizedValue = newValue.map((v: any) => {
        // If already an object with the valueKey, return as-is
        if (v && typeof v === 'object' && v[valueKey!] !== undefined) {
          return v
        }
        // If it's a primitive, find it in dropdownOptions and return the originalOption
        const found = dropdownOptions.find((o) => o.value === v)
        return found ? found.originalOption || found : v
      })

      if (onChange) {
        onChange({ value: normalizedValue, name })
      }
      if (onSelect) {
        onSelect({ value: optionToUse, name })
      }

      // A11Y-1408 / A11Y-1425: Keep menu open only for multi-select with filter so users
      // can select multiple options without the dropdown closing (Placeholder template
      // Course/Major/Minor; Student Profile Plan modal "Select course(s)").
      // Scope: isMulti && showInput only. Single-select still closes; outside click / Escape unchanged.
      if (isMulti && showInput) {
        setTimeout(() => filterInputRef.current?.focus(), 0)
        return
      }

      // Multi-select without filter: also keep menu open so user can pick more
      return
    }

    if (onChange) {
      onChange({ value: optionToUse, name })
    }

    handleMenuClose()
    focusContainer()
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    if (onInputChange) {
      onInputChange({ value: newValue })
    }
    if (inputValue === undefined) {
      setInternalFilterText(newValue)
    }
    // Open menu when user types
    if (newValue && !menuOpen) {
      setMenuOpen(true)
    }
    setFocusedIndex(0)
  }

  // Helper functions for handleKeyDown
  const focusContainer = () => {
    if (containerRef.current) {
      containerRef.current.focus()
    }
  }

  const clearFilterText = useCallback(() => {
    if (onInputChange) {
      onInputChange({ value: '' })
    }
    if (inputValue === undefined) {
      setInternalFilterText('')
    }
  }, [onInputChange, inputValue])

  const selectFocusedOption = () => {
    if (focusedIndex >= 0 && dropdownOptions[focusedIndex]) {
      handleOptionClick(dropdownOptions[focusedIndex])
    }
  }

  const navigateOptions = (direction: 'up' | 'down') => {
    setFocusedIndex((prev) => {
      if (dropdownOptions.length === 0 || dropdownOptions.length === 1) {
        setForceAnnouncementTrigger((curr) => curr + 1)
        return prev
      }
      if (direction === 'down') {
        return prev + 1 >= dropdownOptions.length ? 0 : prev + 1
      }
      return prev - 1 < 0 ? dropdownOptions.length - 1 : prev - 1
    })
  }

  // Key-specific handlers
  const handleArrowDownKey = (event: React.KeyboardEvent, isFilterInputFocused: boolean) => {
    event.preventDefault()

    if (!menuOpen) {
      handleMenuOpen()
      return
    }

    if (isFilterInputFocused) {
      setFocusedIndex(0)
      focusContainer()
    } else {
      navigateOptions('down')
    }
  }

  const handleArrowUpKey = (event: React.KeyboardEvent, isFilterInputFocused: boolean) => {
    event.preventDefault()

    if (isFilterInputFocused) {
      setFocusedIndex(Math.max(dropdownOptions.length - 1, 0))
      focusContainer()
    } else {
      navigateOptions('up')
    }
  }

  const handleEnterKey = (event: React.KeyboardEvent) => {
    event.preventDefault()

    if (!menuOpen) {
      handleMenuOpen()
      return
    }

    if (dropdownOptions.length === 0) {
      handleMenuClose()
    } else {
      selectFocusedOption()
    }
  }

  const handleSpaceKey = (event: React.KeyboardEvent, isFilterInputFocused: boolean) => {
    // When user is typing in filter input, allow spacebar to insert space character
    // instead of selecting an option. This enables entering full names with spaces.
    if (isFilterInputFocused) {
      // Don't prevent default - let the space character be typed
      return
    }

    event.preventDefault()

    if (!menuOpen) {
      handleMenuOpen()
      return
    }

    if (dropdownOptions.length === 0) {
      handleMenuClose()
    } else {
      selectFocusedOption()
    }
  }

  const handleEscapeKey = (event: React.KeyboardEvent, isFilterInputFocused: boolean) => {
    event.preventDefault()

    if (menuOpen) {
      event.stopPropagation()
    }

    if (isFilterInputFocused) {
      clearFilterText()
    }

    handleMenuClose()
    focusContainer()
  }

  const handleTabKey = () => {
    handleMenuClose()
  }

  const handleCharacterKey = (event: React.KeyboardEvent) => {
    if (
      !menuOpen &&
      isFilterable &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault()
      if (onInputChange) {
        onInputChange({ value: event.key })
      }
      if (inputValue === undefined) {
        setInternalFilterText(event.key)
      }
      setMenuOpen(true)
      setFocusedIndex(0)
      setTimeout(() => {
        if (filterInputRef.current) {
          filterInputRef.current.focus()
        }
      }, 0)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return

    // Call parent onKeyDown handler first if provided
    if (onKeyDownProp) {
      onKeyDownProp(event)
    }

    // True whenever the event originated from the filter input itself — covers both
    // the non-multi filterable case and the multi-select with showInput case.
    // Without this, space key events from the multi-select search input would
    // bubble up and trigger option selection instead of inserting a space character.
    const isFilterInputFocused = !!(
      menuOpen &&
      filterInputRef.current &&
      event.target === filterInputRef.current
    )

    switch (event.key) {
      case 'ArrowDown':
        handleArrowDownKey(event, isFilterInputFocused)
        break
      case 'ArrowUp':
        handleArrowUpKey(event, isFilterInputFocused)
        break
      case 'Enter':
        handleEnterKey(event)
        break
      case ' ':
        handleSpaceKey(event, isFilterInputFocused)
        break
      case 'Escape':
        handleEscapeKey(event, isFilterInputFocused)
        break
      case 'Tab':
        handleTabKey()
        break
      default:
        handleCharacterKey(event)
        break
    }
  }

  // Handle blur to close menu when focus leaves the dropdown
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      // Use wrapperRef (which contains both the combobox and the menu) so that
      // focus moving between the input and menu options does not count as leaving.
      const focusStayedInside =
        wrapperRef.current && wrapperRef.current.contains(e.relatedTarget as Node)

      if (focusStayedInside) {
        return
      }

      // Call parent onBlur handler if provided
      if (onBlurProp) {
        onBlurProp(e)
      }

      // Close menu if focus moved outside the dropdown entirely
      if (menuOpen && containerRef.current) {
        handleMenuClose()
      }
    },
    [menuOpen, handleMenuClose, onBlurProp]
  )

  // Prevent blur when clicking inside the menu
  const handleMenuMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  // Shared onBlur handler for all filter/search inputs
  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (
        !menuRef.current ||
        (!menuRef.current.contains(e.relatedTarget as Node) &&
          e.relatedTarget !== containerRef.current)
      ) {
        if (clearInputOnBlur) {
          clearFilterText()
        }
        handleMenuClose()
      }
    },
    [clearInputOnBlur, clearFilterText, handleMenuClose]
  )

  useEffect(() => {
    if (menuOpen && dropdownOptions.length === 0) {
      setAnnouncement(filterText ? 'No options match your search' : 'No options available')
    } else if (menuOpen && focusedIndex >= 0 && dropdownOptions[focusedIndex]) {
      const focusedOption = dropdownOptions[focusedIndex]

      if (isMulti) {
        const isSelected = selectedValues.some((v: any) => {
          const vValueKey =
            v && typeof v === 'object' && v[valueKey!] !== undefined ? v[valueKey!] : v
          const focusedValueKey =
            focusedOption.originalOption && focusedOption.originalOption[valueKey!] !== undefined
              ? focusedOption.originalOption[valueKey!]
              : focusedOption.value
          return vValueKey === focusedValueKey
        })
        const statusText = isSelected ? 'selected' : 'not selected'
        setAnnouncement(`${focusedOption.label}, ${statusText}`)
      } else {
        setAnnouncement(focusedOption.label)
      }
    } else if (!menuOpen) {
      setAnnouncement('')
    }
  }, [menuOpen, focusedIndex, dropdownOptions, selectedValues, isMulti, filterText, valueKey])

  useEffect(() => {
    if (forceAnnouncementTrigger > 0 && menuOpen) {
      let message

      if (dropdownOptions.length === 0) {
        message = filterText ? 'No options match your search' : 'No options available'
      } else if (focusedIndex >= 0 && dropdownOptions[focusedIndex]) {
        const focusedOption = dropdownOptions[focusedIndex]

        if (dropdownOptions.length === 1) {
          message = `${focusedOption.label}, only option available`
        } else {
          message = focusedOption.label
        }
      }

      if (message) {
        const uniqueMessage = message + '\u200B'.repeat(forceAnnouncementTrigger)
        setAnnouncement(uniqueMessage)
      }
    }
  }, [forceAnnouncementTrigger, menuOpen, focusedIndex, dropdownOptions, filterText])

  const renderDisplayValue = () => {
    // When menu is open and filterable, show the filter input for single-select dropdowns (A11Y-1408).
    // Multi-select with showInput is handled below in the isMulti block which keeps chips visible
    // alongside the search input.
    if ((isFilterable || showInput) && menuOpen && !(isMulti && showInput)) {
      return (
        <div className="crane-select-inner-input">
          <input
            ref={filterInputRef}
            id={inputId}
            className="crane-select-filter-input"
            aria-label={label ? `Filter ${label}` : 'Filter options'}
            value={filterText}
            onChange={handleFilterChange}
            onBlur={handleInputBlur}
            placeholder={placeholder !== undefined ? placeholder : 'Type to filter...'}
          />
        </div>
      )
    }

    if (isMulti) {
      const selectedCount = selectedOptions ? (selectedOptions as OptionType[]).length : 0
      const totalCount = options?.length || 0

      const multiSearchInput = showInput ? (
        <input
          ref={filterInputRef}
          id={inputId}
          className="crane-select-filter-input"
          aria-label={label ? `Filter ${label}` : 'Filter options'}
          value={filterText}
          autoComplete="off"
          onChange={handleFilterChange}
          onBlur={(e) => {
            if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
              handleMenuClose()
            }
          }}
          placeholder={placeholder !== undefined ? placeholder : ''}
        />
      ) : null

      // When a valueComponent (chips) is provided, always render the chips container
      // so that multiSearchInput stays in the same DOM position regardless of
      // how many chips are selected. Moving the input between DOM locations causes
      // React to unmount/remount it, which fires blur → closes the menu.
      if (valueComponent) {
        return (
          <div
            ref={valueGroupRef}
            className="crane-select-value-group"
            style={
              selectedCount > 0
                ? {
                    overflow: 'visible',
                    whiteSpace: 'normal',
                    textOverflow: 'clip',
                    minHeight: 'fit-content',
                    height: 'auto'
                  }
                : undefined
            }
          >
            <div className="crane-select-inner-input-flex">
              {selectedCount > 0 &&
                (selectedOptions as OptionType[]).map((option) =>
                  option ? valueComponent({ option: option.originalOption || option }) : null
                )}
              {multiSearchInput}
            </div>
          </div>
        )
      }

      // Compute display text for selected values (used in both open and closed states)
      let displayText = ''
      if (selectedCount <= 3) {
        displayText = (selectedOptions as OptionType[])
          .filter((option) => option && option.label)
          .map((option) => option.label)
          .join(delimiter || ', ')
      } else if (selectedCount === totalCount) {
        displayText = 'All Selected'
      } else {
        displayText = `${selectedCount} Selected`
      }

      // No valueComponent: when menu is open and showInput, show selected labels + filter input;
      // when menu is closed, show selected labels only.
      if (showInput && menuOpen) {
        return (
          <div className="crane-select-value-group">
            {selectedOptions && selectedCount > 0 && (
              <span className="crane-select-multi-value-text">{displayText}</span>
            )}
            {multiSearchInput}
          </div>
        )
      }

      return (
        <div className="crane-select-value-group">
          {selectedOptions && selectedCount > 0 ? (
            <span className="crane-select-multi-value-text">{displayText}</span>
          ) : (
            <span className="crane-select-placeholder">
              {placeholder !== undefined ? placeholder : 'Select...'}
            </span>
          )}
        </div>
      )
    }

    return (
      <div className="crane-select-value-group">
        {selectedOptions ? (
          <span>
            {typeof valueRenderer === 'function'
              ? valueRenderer({
                  option:
                    (selectedOptions as OptionType).originalOption ||
                    (selectedOptions as OptionType)
                })
              : (selectedOptions as OptionType).label}
          </span>
        ) : (
          <span className="crane-select-placeholder">
            {placeholder !== undefined ? placeholder : 'Select...'}
          </span>
        )}
      </div>
    )
  }

  // Group options for rendering with headers
  const renderGroupedOptions = () => {
    if (!grouped || !dropdownOptions.length) {
      return dropdownOptions.map((option, index) => {
        const isSelected = isMulti
          ? selectedValues.some((v: any) => {
              const vValueKey = v && v[valueKey!] !== undefined ? v[valueKey!] : v
              const optionValueKey =
                option.originalOption && option.originalOption[valueKey!] !== undefined
                  ? option.originalOption[valueKey!]
                  : option.value
              return vValueKey === optionValueKey
            })
          : selectedOptions && (selectedOptions as OptionType).value === option.value
        const isFocused = index === focusedIndex
        const className = `crane-select-option ${isSelected ? 'selected' : ''} ${
          isFocused ? 'focused' : ''
        }`.trim()

        return (
          <div
            key={`option-${option.value}`}
            id={id ? `${id}-option-${index}` : undefined}
            className={className}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              handleOptionClick(option)
            }}
            onMouseEnter={() => {
              setFocusedIndex(index)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleOptionClick(option)
              }
            }}
            role="option"
            aria-selected={isSelected}
            tabIndex={-1}
          >
            {isMulti && (
              <span className="crane-select-option-checkbox">
                {isSelected ? (
                  <CheckBoxIcon fontSize="small" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" />
                )}
              </span>
            )}
            {optionRenderer ? (
              <span>
                {optionRenderer({
                  getOptionLabel,
                  labelKey,
                  option: option.originalOption || option,
                  selected: isSelected
                })}
              </span>
            ) : (
              <span>{option.label}</span>
            )}
          </div>
        )
      })
    }

    // Group options by their group property
    const groupedOptionsMap = new Map<string, OptionType[]>()
    dropdownOptions.forEach((option) => {
      const groupName = option.group || 'Other'
      if (!groupedOptionsMap.has(groupName)) {
        groupedOptionsMap.set(groupName, [])
      }
      groupedOptionsMap.get(groupName)!.push(option)
    })

    const result: JSX.Element[] = []
    let optionIndex = 0

    Array.from(groupedOptionsMap.entries()).forEach(([groupName, groupOptions]) => {
      // Add group header
      result.push(
        <div
          key={`group-${groupName}`}
          className="crane-select-group-header"
          role="group"
          aria-label={groupName}
        >
          {groupName}
        </div>
      )

      // Add options for this group
      groupOptions.forEach((option) => {
        const isSelected = isMulti
          ? selectedValues.some((v: any) => {
              const vValueKey = v && v[valueKey!] !== undefined ? v[valueKey!] : v
              const optionValueKey =
                option.originalOption && option.originalOption[valueKey!] !== undefined
                  ? option.originalOption[valueKey!]
                  : option.value
              return vValueKey === optionValueKey
            })
          : selectedOptions && (selectedOptions as OptionType).value === option.value
        const isFocused = optionIndex === focusedIndex
        const className = `crane-select-option ${isSelected ? 'selected' : ''} ${
          isFocused ? 'focused' : ''
        }`.trim()
        const currentIndex = optionIndex

        result.push(
          <div
            key={`grouped-option-${option.value}`}
            id={id ? `${id}-option-${groupName}-${option.value}` : undefined}
            className={className}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              handleOptionClick(option)
            }}
            onMouseEnter={() => {
              setFocusedIndex(currentIndex)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleOptionClick(option)
              }
            }}
            role="option"
            aria-selected={isSelected}
            tabIndex={-1}
          >
            {isMulti && (
              <span className="crane-select-option-checkbox">
                {isSelected ? (
                  <CheckBoxIcon fontSize="small" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" />
                )}
              </span>
            )}
            {optionRenderer ? (
              optionRenderer({
                getOptionLabel,
                labelKey,
                option: option.originalOption || option
              })
            ) : (
              <span>{option.label}</span>
            )}
          </div>
        )
        optionIndex += 1
      })
    })

    return result
  }

  const containerClassName = `crane-select${disabled ? ' disabled' : ''}${menuOpen ? ' open' : ''}${
    styleVariant ? ` crane-select-${styleVariant}` : ''
  }`

  // WCAG 4.1.2: Compute aria-label with proper priority
  // Use aria-labelledby if provided (takes precedence per ARIA spec), otherwise fall back to ariaLabel > label > placeholder > default
  const dropdownAriaLabel = ariaLabelledBy
    ? undefined
    : ariaLabel || label || placeholder || 'Select options'

  return (
    <div
      ref={wrapperRef}
      id={id || undefined}
      className={containerClassName}
      style={{ position: 'relative' }}
    >
      {/* Screen reader announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        role="status"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {announcement}
      </div>
      {/* eslint-disable jsx-a11y/role-has-required-aria-props -- aria-controls intentionally omitted: poor screen reader support */}
      <div
        ref={containerRef}
        className="crane-select-input-group"
        style={
          valueComponent &&
          isMulti &&
          selectedOptions &&
          (selectedOptions as OptionType[]).length > 0
            ? {
                minHeight: '0',
                height: 'fit-content',
                alignItems: 'flex-start',
                paddingBottom: '8px'
              }
            : undefined
        }
        onClick={(e) => {
          if (disabled) return
          if (clearJustFiredRef.current) {
            clearJustFiredRef.current = false
            return
          }
          const clickTarget = e.target as HTMLElement
          const isTextFieldTarget =
            clickTarget.tagName === 'INPUT' ||
            clickTarget.tagName === 'TEXTAREA' ||
            clickTarget.isContentEditable
          e.stopPropagation()
          e.preventDefault()
          // For multi-select with showInput, open menu and focus the text input
          if (isMulti && showInput) {
            if (!menuOpen) handleMenuOpen()
            setTimeout(() => {
              if (filterInputRef.current) filterInputRef.current.focus()
            }, 0)
            return
          }
          // For filterable dropdowns without a text input, allow toggle (open/close)
          // For filterable dropdowns with showInput, only open (user types in the input)
          if (isFilterable && !menuOpen) {
            handleMenuOpen()
          } else if (isFilterable && menuOpen && !showInput) {
            // If the click originated inside the inner filter input rendered
            // by renderDisplayValue, skip the close. Without this guard the
            // native caret/selection behaviour (restored by the onMouseDown
            // guard) would be immediately undone by closing + re-rendering,
            // which would break "click mid-string to position the caret".
            if (isTextFieldTarget) return
            handleMenuClose()
          } else if (!isFilterable) {
            handleMenuToggle(e)
          }
        }}
        onMouseDown={(e) => {
          // preventDefault on mousedown cancels the browser's native
          // caret-positioning and text-selection behaviour. Skip it when the
          // click lands inside an actual text field (e.g. the inner filter
          // input rendered by renderDisplayValue), so users can click
          // mid-string to position the caret. The preventDefault is still
          // needed for clicks on the wrapper/chevron to keep focus on the
          // combobox.
          const target = e.target as HTMLElement
          if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
          ) {
            return
          }
          e.stopPropagation()
          e.preventDefault()
        }}
        onBlur={handleBlur}
        onFocus={(e) => {
          if (onFocusProp) {
            onFocusProp(e)
          }
        }}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={menuOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled ? 'true' : undefined}
        aria-labelledby={ariaLabelledBy || undefined}
        aria-label={dropdownAriaLabel}
        aria-activedescendant={
          menuOpen && focusedIndex >= 0 && dropdownOptions[focusedIndex] && id
            ? `${id}-option-${focusedIndex}`
            : undefined
        }
      >
        {beforeInput}
        {renderDisplayValue()}

        <div
          className="crane-select-indicators"
          style={
            valueComponent &&
            isMulti &&
            selectedOptions &&
            (selectedOptions as OptionType[]).length > 0
              ? { alignSelf: 'flex-start', paddingTop: '18px' }
              : undefined
          }
        >
          {!beforeInput && (
            <button
              type="button"
              className="crane-select-toggle-button"
              onClick={handleMenuToggle}
              onMouseDown={(e) => e.preventDefault()}
              aria-labelledby={ariaLabelledBy || undefined}
              aria-label={dropdownAriaLabel}
              tabIndex={-1}
            >
              {menuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </button>
          )}
        </div>
      </div>
      {/* eslint-enable jsx-a11y/role-has-required-aria-props */}

      {onClear && !isMulti && selectedOptions && !disabled && (
        <button
          type="button"
          className="crane-select-clear-button"
          style={{
            position: 'absolute',
            right: '32px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}
          onClick={() => {
            clearJustFiredRef.current = true
            onClear()
          }}
          aria-label="Clear selection"
        >
          <CloseIcon fontSize="small" />
        </button>
      )}

      {menuOpen && (
        <div
          ref={menuRef}
          className="crane-select-menu-container"
          role="listbox"
          aria-multiselectable={isMulti}
          aria-label={menuAriaLabel || 'Available options'}
          tabIndex={-1}
          onMouseDown={handleMenuMouseDown}
          style={(() => {
            if (menuPosition) {
              return {
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                width: `${menuPosition.width}px`
              }
            }
            if (styleVariant === 'transparent' && containerRef.current) {
              const valueGroup = containerRef.current.querySelector(
                '.crane-select-value-group'
              ) as HTMLElement | null
              const triggerWidth = valueGroup
                ? Math.ceil(valueGroup.getBoundingClientRect().width)
                : 0
              return {
                minWidth: triggerWidth > 0 ? `${triggerWidth}px` : undefined
              }
            }
            return undefined
          })()}
        >
          {renderGroupedOptions()}

          {dropdownOptions.length === 0 && (
            <div className="crane-select-no-options">
              {filterText ? 'No options match your search' : 'No options available'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UnifiedDropdown
